import type { SocialApi } from '../api/social';

// Who the viewer follows, as one cached set with change notifications (H16/D204).
//
// The follow buttons live on the plaque rail and the end screen — both render from
// synchronous state, so neither can await a network round-trip per paint. This service
// owns the one fetch (`GET /social/following`, on demand) and the optimistic write:
// tapping Follow flips the set immediately, the POST rides behind, and a failure flips
// it back. The button is a promise the server almost always keeps; when it does not,
// the honest state returns and the player can tap again.

export interface FollowService {
  /** Synchronous read for render paths. Unknown ids are simply "not following". */
  isFollowing(accountId: string): boolean;
  /** Optimistic follow; safe to call twice (the second is a no-op). */
  follow(accountId: string): void;
  onChange(fn: () => void): () => void;
  /** Load the real set — call when a game screen mounts and the viewer is signed in. */
  refresh(): Promise<void>;
}

export function createFollowService(deps: { api: SocialApi; signedIn: () => boolean; onFollow?: () => void }): FollowService {
  const following = new Set<string>();
  const listeners = new Set<() => void>();
  const notify = (): void => {
    for (const fn of [...listeners]) fn();
  };

  return {
    isFollowing: (accountId) => following.has(accountId),
    follow(accountId): void {
      if (!deps.signedIn() || following.has(accountId)) return;
      following.add(accountId); // optimistic: the tap answers the tap
      deps.onFollow?.(); // D212: the loop beacon counts follows, never who was followed
      notify();
      deps.api.follow(accountId).catch(() => {
        following.delete(accountId); // …and a refused write un-answers it honestly
        notify();
      });
    },
    onChange(fn): () => void {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    async refresh(): Promise<void> {
      if (!deps.signedIn()) return;
      try {
        const list = await deps.api.following();
        following.clear();
        for (const p of list) following.add(p.id);
        notify();
      } catch {
        // offline: the empty/stale set stands — a follow button is never worth an error
      }
    },
  };
}
